<script setup lang="ts">
import { useQuery } from '@tanstack/vue-query'
import { fetchInvoiceQueryOptions } from '@/features/invoices/queries'
import InvoiceSkeleton from '@/features/invoices/components/partials/InvoiceSkeleton.vue'
import InvoiceSkeletonInvoiceDetails from '@/features/invoices/components/partials/InvoiceSkeletonInvoiceDetails.vue'
import PageContent from '@/components/page-content.vue'
import InvoiceSkeletonBankingDetails from '@/features/invoices/components/partials/InvoiceSkeletonBankingDetails.vue'
import InvoiceSkeletonSummary from '@/features/invoices/components/partials/InvoiceSkeletonSummary.vue'
import { computed, reactive, ref, useTemplateRef, watchEffect } from 'vue'
import { watchOnce } from '@vueuse/core'
import InvoiceSkeletonOwnerDetails from '@/features/invoices/components/partials/InvoiceSkeletonOwnerDetails.vue'
import InvoiceSkeletonBillTotal from '@/features/invoices/components/partials/InvoiceSkeletonBillTotal.vue'
import InvoiceSkeletonCharges from '@/features/invoices/components/partials/InvoiceSkeletonCharges.vue'
import InvoiceSkeletonMeterReadings from '@/features/invoices/components/partials/InvoiceSkeletonMeterReadings.vue'
import InvoiceSkeletonCustomerDetails from '@/features/invoices/components/partials/InvoiceSkeletonCustomerDetails.vue'
import ButtonPrimary from '@/components/button-primary.vue'
import { faFloppyDisk, faHandPointUp, faXmark } from '@fortawesome/pro-regular-svg-icons'
import { useUpdateInvoice, useUpdateInvoiceCharges, useUpdateInvoiceCategories } from '@/features/invoices/mutations'
import useLoader from '@/composables/loader'
import useNotifications from '@/composables/notifications'
import { ChargeType, InvoiceStatusNumeric } from '@/features/invoices/constants'
import { useRouter } from 'vue-router'
import { useApproveInvoice, useSaveNotes, useUpdateInvoiceMeterReadings } from '@/composables/billing/invoices'
import ButtonSecondary from '@/components/button-secondary.vue'
import ButtonHighlighted from '@/components/button-highlighted.vue'
import ModalEscalationWarning from '@/views/billing/invoicing/modal-escalation-warning.vue'
import ContentLoader from '@/components/content-loader.vue'
import HeroSplashError from '@/components/hero-splash-error.vue'
import ModalConfirmApproveAndSend from '@/views/billing/invoicing/modal-confirm-approve-and-send.vue'
import { useModalState } from '@/shared/composables/useModalState'
import InvoiceSkeletonNotes from '@/features/invoices/components/partials/InvoiceSkeletonNotes.vue'

const props = defineProps<{ invoiceId: number }>()
const { data: invoice, isError, error, isFetching } = useQuery(fetchInvoiceQueryOptions(props.invoiceId))

const router = useRouter()
const { wrap } = useLoader()
const { addSuccess, addError } = useNotifications()
const { open: showApproveAndSendConfirmation, isOpen: isConfirmingApproveAndSend, close: hideApproveAndSendConfirmation } = useModalState()
const { mutateAsync: approveInvoiceAsync } = useApproveInvoice()
const { mutateAsync: updateInvoiceAsync } = useUpdateInvoice(props.invoiceId)
const { mutateAsync: updateCategoriesAsync } = useUpdateInvoiceCategories(props.invoiceId)
const { mutateAsync: updateChargesAsync } = useUpdateInvoiceCharges(props.invoiceId)
const { mutateAsync: updateMeterReadingsAsync } = useUpdateInvoiceMeterReadings(props.invoiceId)
const { mutateAsync: updateNotesAsync } = useSaveNotes(props.invoiceId)
const chargesComponent = useTemplateRef<InstanceType<typeof InvoiceSkeletonCharges>>('charges')
const metersComponent = useTemplateRef<InstanceType<typeof InvoiceSkeletonMeterReadings>>('meters')
const showSavings = ref(true)
const poNumber = ref('')
const showMeters = ref(true)
const notes = ref<string|null>(null)
const initialized = ref(false)

const escalationWarning = ref<string>()
const flattenedCharges = computed(() => invoice.value?.categories.flatMap(x => x.charges) ?? [])

// Update some values when the invoice loads initially.
const stopInit = watchEffect(
  () => {
    if (initialized.value || invoice.value === undefined) {
      if (initialized.value) {
        stopInit?.()
      }
      return
    }

    poNumber.value = invoice.value.poNumber
    showSavings.value = invoice.value.showSavings
    showMeters.value = invoice.value.showMeterReadings
    notes.value = invoice.value.invoiceNotes
    initialized.value = true
  }
)
watchOnce(
  invoice,
  newValue => {
    if (newValue === undefined)
      return

    poNumber.value = newValue.poNumber
    showSavings.value = newValue.showSavings
    showMeters.value = newValue.showMeterReadings
    notes.value = newValue.invoiceNotes
  }
)

const navigateToInvoiceDashboard = () => router.push({ name: 'invoices.list' })

function approveInvoice() {
  return approveInvoiceAsync(props.invoiceId)
    .then(
      () => {
        addSuccess({ title: 'Invoice approved successfully.' })
      },
      (err) => {
        const escalationTaskError = err.errors.filter((e: { source: { pointer: string } }) => e.source.pointer == 'EscalationId')[0]

        if (escalationTaskError) {
          escalationWarning.value = escalationTaskError.title
        } else {
          addError({ title: 'Invoice could not be approved.' })
        }
      }
    )
}

async function persistChanges() {
  const statusBeforeSave = invoice.value?.invoiceStatusId

  const changedMeters = metersComponent.value?.changes() ?? []
  const changedCharges = chargesComponent.value?.changes().charges ?? []
  const changedCategories = chargesComponent.value?.changes().categories ?? []

  await wrap(
    async () => {
      const promises = [
        updateInvoiceAsync({
          showSavings: showSavings.value,
          poNumber: poNumber.value,
          showMeterReadings: showMeters.value,
        })
      ]

      if (notes.value !== null) {
        promises.push(updateNotesAsync(notes.value))
      }

      if (changedCategories.length > 0) {
        promises.push(
          updateCategoriesAsync(
            changedCategories.map(
              x => ({
                ...x.values,
                id: x.id,
              })
            )
          )
        )
      }

      if (changedCharges.length > 0) {
        promises.push(
          updateChargesAsync(
            changedCharges.map(
              x => ({
                ...(flattenedCharges.value.find(y => y.id === x.id) ?? {}),
                ...x.values,
                id: x.id,
              })
            )
          )
        )
      }

      if (changedMeters.length > 0) {
        promises.push(
          updateMeterReadingsAsync(
            changedMeters.map(
              changed => {
                const existing = invoice.value!.meterReadings.find(meter => meter.id === changed.id)!

                return {
                  ...existing,
                  ...changed.values,
                  id: changed.id,
                }
              }
            )
          )
        )
      }

      await Promise.all(promises)

      if (statusBeforeSave !== InvoiceStatusNumeric.Draft) {
        await navigateToInvoiceDashboard()
      }
    }
  ).then(
    () => {
      addSuccess({ title: 'Invoice updated successfully' })

      metersComponent.value?.clearChanges()
      chargesComponent.value?.clearChanges()
    },
    (e) => {
      addError({ title: 'Unable to update invoice', message: 'Please try again' })
    }
  )
}
</script>

<template>
  <PageContent>
    <ModalEscalationWarning v-if="escalationWarning !== undefined" :message="escalationWarning" @close="escalationWarning = undefined" />
    <ModalConfirmApproveAndSend v-if="isConfirmingApproveAndSend"
                                @close="hideApproveAndSendConfirmation"
                                :invoice-id="invoiceId" />

    <ContentLoader v-if="isFetching" :loading="isFetching" />
    <HeroSplashError v-else-if="isError" />
    <InvoiceSkeleton v-else-if="invoice" title="Tax Invoice">
      <template #footer>
        <ButtonPrimary @click="persistChanges()" :icon="faFloppyDisk">Save</ButtonPrimary>
        <ButtonSecondary :icon="faXmark" @click="navigateToInvoiceDashboard">Cancel</ButtonSecondary>
        <template v-if="invoice.invoiceStatusId === InvoiceStatusNumeric.Draft">
          <div class="vr"></div>
          <ButtonHighlighted :icon="faHandPointUp" @click="approveInvoice">Approve</ButtonHighlighted>
          <ButtonSecondary outline="teal" :icon="faHandPointUp" @click="showApproveAndSendConfirmation">Approve & Send</ButtonSecondary>
        </template>
      </template>

      <template #charges>
        <InvoiceSkeletonCharges ref="charges"
                                :editable-charge-types="[InvoiceStatusNumeric.Draft, InvoiceStatusNumeric.ActionRequired].includes(invoice.invoiceStatusId) ? [ChargeType.Usage, ChargeType.Recurring] : []"
                                :total-excluding-tax="invoice.totalAmountExcludingTax"
                                :total-including-tax="invoice.totalAmountIncludingTax"
                                :tax="invoice.tax"
                                :tax-percentage="invoice.taxPercentage"
                                :categories="invoice.categories"
                                :asset-code="invoice.assetCode" />
      </template>

      <template #meters>
        <InvoiceSkeletonMeterReadings ref="meters"
                                      :invoice-id="invoiceId"
                                      :invoice-status="invoice.invoiceStatusId"
                                      :exported="invoice.isExportPower"
                                      v-model:show="showMeters"
                                      :grid-consumption="invoice.totalGridConsumption"
                                      :solar-consumption="invoice.totalEnergyConsumption"
                                      :exported-energy="invoice.totalEnergyExported"
                                      :meters="invoice.meterReadings" />
      </template>

      <template #customer-details>
        <InvoiceSkeletonCustomerDetails :name="invoice.customer.name"
                                        :address="[
                                          invoice.customer.addressLineOne,
                                          invoice.customer.addressLineTwo,
                                          invoice.customer.suburb,
                                          invoice.customer.city,
                                          invoice.customer.region,
                                          invoice.customer.country,
                                        ]"
                                        :vat-number="invoice.customer.vatNumber"
                                        v-model:po-number="poNumber" />
      </template>

      <template #bill-total>
        <InvoiceSkeletonBillTotal :amount="invoice.totalAmountIncludingTax"
                                  :due-date="invoice.dueDate" />
      </template>

      <template #owner-details>
        <InvoiceSkeletonOwnerDetails :name="invoice.contractFundEntity.name"
                                     :address="invoice.contractFundEntity.address"
                                     :logo-url="invoice.contractFundEntity.logoUrl"
                                     :vat-number="invoice.contractFundEntity.vatNumber" />
      </template>

      <template #comments>
        <InvoiceSkeletonNotes v-model:content="notes" />
      </template>

      <template #invoice-details>
        <InvoiceSkeletonInvoiceDetails :invoice-number="invoice.invoiceNumber"
                                       :date="invoice.invoiceDate"
                                       :period="[invoice.periodStartDate, invoice.periodEndDate]"
                                       :date-format="invoice.invoiceDateFormat" />
      </template>
      <template #banking-details>
        <InvoiceSkeletonBankingDetails :account-holder="invoice.contractFundEntity.accountName"
                                       :bank="invoice.contractFundEntity.bank"
                                       :account-number="invoice.contractFundEntity.accountNumber"
                                       :branch-code="invoice.contractFundEntity.branchCode" />
      </template>

      <template #summary>
        <InvoiceSkeletonSummary v-model:show="showSavings"
                                :savings-to-date="invoice.totalSavingsToDate"
                                :savings="invoice.invoiceSavings?.amount ?? 0"
                                :amount-exc-tax="invoice.totalAmountExcludingTax" />
      </template>
    </InvoiceSkeleton>
  </PageContent>
</template>

<style scoped lang="scss">

</style>
